import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import useRecordImage from '$tablet-app/src/app/images/hooks/useRecordImage';
import { palette } from '$tablet-app/src/app/theme';
import { Button } from '@akin-home/buttons/native';
import { ContactModel } from '@akin-home/contacts';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';

interface Props {
  contact: ContactModel;

  isFavourite?: boolean;

  onClose?: () => void;

  onEdit?: (contact: ContactModel) => void;

  onFavourite?: (contact: ContactModel) => void;

  onCallPhone?: (number: string) => void;

  onPressVideoCall?: (contact: ContactModel) => void;

  onPressMessageContact: (contact: ContactModel) => void;
}

const image_edits = {
  resize: {
    width: 107,
    height: 107,
    fit: 'cover',
  },
};

const ContactDetailsModal = (props: Props) => {
  const { contact, onClose, onCallPhone, onPressMessageContact } = props;

  const { source } = useRecordImage({ record: contact, edits: image_edits });

  const onPressEdit = () => props.onEdit?.(contact);
  const onPressFavourite = () => props.onFavourite?.(contact);

  const onPressCallNumber = (number: string) => () => {
    sendBackgroundMessage(intent_labels.call_contact, {
      context: { contact: { id: contact.id, name: contact.name }, number },
    });

    onCallPhone?.(number);
  };

  return (
    <Container>
      <Row justifyContent="space-between">
        <Button variant="secondary" icon="times" onPress={onClose} />

        <Buttons>
          <Button variant="secondary" icon="edit" onPress={onPressEdit} />

          <Button
            variant="secondary"
            icon="heart"
            iconColor={props.isFavourite ? palette.Red : palette.neutral_90}
            onPress={onPressFavourite}
          />
        </Buttons>
      </Row>

      <ScrollView contentContainerStyle={styles.scroll_container}>
        {source ? (
          <Row justifyContent="center">
            <Image source={source} />
          </Row>
        ) : null}

        <Row justifyContent="center">
          <Text variant="h1" weight="semibold">
            {contact.name}
          </Text>
        </Row>

        <TopButtons>
          {contact.hasPhoneNumber() ? (
            <Button
              size="large"
              variant="secondary"
              icon="phone"
              iconSize={20}
              iconColor={palette.primary_90}
              onPress={onPressCallNumber(contact.phoneNumbers[0].number)}
            />
          ) : null}

          <Button
            size="large"
            variant="secondary"
            icon="video"
            iconSize={20}
            iconColor={palette.Secondary_blue}
            onPress={() => props.onPressVideoCall?.(contact)}
          />

          {contact.hasPhoneNumber() ? (
            <Button
              size="large"
              variant="secondary"
              icon="comment-lines"
              iconSize={20}
              iconColor={palette.Bright_green}
              onPress={() => onPressMessageContact(contact)}
            />
          ) : null}
        </TopButtons>

        {contact.emailAddresses.length ? (
          <>
            <Spacer height="2px" color="lightgrey" />

            <Row>
              <Text variant="h3" weight="semibold">
                Email:
              </Text>

              <Spacer width="16px" />

              <Emails>
                {contact.emailAddresses.map((email, idx) => (
                  <Text key={idx}>{email}</Text>
                ))}
              </Emails>
            </Row>
          </>
        ) : null}

        {contact.phoneNumbers.map((number, idx) => (
          <React.Fragment key={idx}>
            <Spacer height="2px" color="lightgrey" />

            <Row justifyContent="space-between" alignItems="center">
              <View>
                <Text variant="h3" weight="semibold">
                  {number.number}
                </Text>
                <Text variant="p2">{number.type}</Text>
              </View>

              <Buttons>
                <Button
                  size="large"
                  variant="secondary"
                  icon="phone"
                  iconSize={20}
                  iconColor={palette.primary_90}
                  onPress={onPressCallNumber(contact.phoneNumbers[0].number)}
                />

                <Button
                  size="large"
                  variant="secondary"
                  icon="comment-lines"
                  iconSize={20}
                  iconColor={palette.Bright_green}
                  onPress={() => onPressMessageContact(contact)}
                />
              </Buttons>
            </Row>
          </React.Fragment>
        ))}

        {contact.addresses?.length ? (
          <>
            <Spacer height="2px" color="lightgrey" />
            <Text variant="h3" weight="semibold">
              Addresses
            </Text>
          </>
        ) : null}

        {contact.addresses?.map((address, idx) => (
          <View key={idx}>
            <Text>{address.street}</Text>
            <Text>{address.city}</Text>
            <Text>{address.postcode}</Text>
            <Text>{address.state}</Text>
            <Text>{address.country}</Text>
          </View>
        ))}
      </ScrollView>
    </Container>
  );
};

const Container = styled.View`
  height: 100%;
  padding: 16px;
  gap: 16px;
`;

const styles = StyleSheet.create({
  scroll_container: {
    gap: 16,
  },
});

const Image = styled.Image`
  height: 107px;
  width: 107px;
  border-radius: 30px;
`;

const Buttons = styled.View`
  flex-direction: row;
  gap: 16px;
`;

const TopButtons = styled(Buttons)`
  justify-content: center;
`;

const Emails = styled.View`
  padding-top: 5px;
  gap: 10px;
`;

export default ContactDetailsModal;
